import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import { useEffect } from 'react';
// Advanced Filters Modal with date picker support
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  MenuItem,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import FormProvider, { RHFTextField, RHFSelect, RHFAutocompleteAsync } from 'src/components/hook-form';

export default function AdvancedFiltersModal({ open, onClose, config, filters, onFilters }) {
  const methods = useForm({
    defaultValues: filters,
  });

  const { handleSubmit, reset } = methods;

  const advancedFields = config.fields.filter((field) => field.inAdvancedFilters);

  // Sync form values with filter props when they change
  useEffect(() => {
    reset(filters);
  }, [filters, reset]);

  const onSubmit = handleSubmit((data) => {
    onFilters(data);
    onClose();
  });

  const handleReset = () => {
    // Reset form to default values
    reset(config.default);
    // Also update the filters immediately
    onFilters(config.default);
  };

  const renderField = (field) => {
    switch (field.type) {
      case 'dateRange':
        return (
          <LocalizationProvider dateAdapter={AdapterDateFns} key={field.name}>
            <Stack spacing={2}>
              <Controller
                name={`${field.name}.start`}
                control={methods.control}
                render={({ field: { value, onChange } }) => (
                  <DatePicker
                    label={`${field.label} (Start)`}
                    value={value || null}
                    onChange={onChange}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                )}
              />
              <Controller
                name={`${field.name}.end`}
                control={methods.control}
                render={({ field: { value, onChange } }) => (
                  <DatePicker
                    label={`${field.label} (End)`}
                    value={value || null}
                    onChange={onChange}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                )}
              />
            </Stack>
          </LocalizationProvider>
        );
  
      case 'numberRange':
        return (
          <Stack key={field.name} spacing={2} direction="row">
            <RHFTextField
              name={`${field.name}.min`}
              label={`${field.label} (Min)`}
              type="number"
            />
            <RHFTextField
              name={`${field.name}.max`}
              label={`${field.label} (Max)`}
              type="number"
            />
          </Stack>
        );

      case 'select':
        return (
          <RHFSelect key={field.name} name={field.name} label={field.label}>
            {field.options.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </RHFSelect>
        );

      case 'date': {
        // Helper function to get appropriate helper text
        const getDateHelperText = (fieldName) => {
          if (fieldName === 'endDate') return 'Must be >= start date';
          if (fieldName === 'startDate') return 'Start date for filtering';
          return undefined;
        };

        return (
          <Controller
            key={field.name}
            name={field.name}
            control={methods.control}
            render={({ field: { onChange, value } }) => (
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label={field.label}
                  format="dd/MM/yyyy"
                  value={value || null}
                  onChange={onChange}
                  minDate={field.name === 'end_date' && methods.watch('start_date')
                    ? methods.watch('start_date')
                    : undefined}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      helperText: getDateHelperText(field.name)
                    }
                  }}
                />
              </LocalizationProvider>
            )}
          />
        );
      }

      case 'autocomplete':
        return (
          <RHFAutocompleteAsync
            key={field.name}
            name={field.name}
            label={field.label}
            apiEndpoint={field.apiEndpoint}
            optionLabel={field.optionLabel}
            optionValue={field.optionValue}
            placeholder={field.placeholder}
            mapToId={false}
            dependsOn={field.dependsOn}
            multiple={field.multiple}
          />
        );

      default:
        return (
          <RHFTextField
            key={field.name}
            name={field.name}
            label={field.label}
            placeholder={field.placeholder}
          />
        );
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Advanced Filters</DialogTitle>
      <FormProvider methods={methods} onSubmit={onSubmit}>
        <DialogContent>
          <Stack spacing={3} sx={{ pt: 1 }}>
            {advancedFields.map(renderField)}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleReset} color="inherit">
            Reset
          </Button>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained">
            Apply Filters
          </Button>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
}

AdvancedFiltersModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  config: PropTypes.shape({
    fields: PropTypes.arrayOf(PropTypes.object).isRequired,
    default: PropTypes.object.isRequired,
  }).isRequired,
  filters: PropTypes.object.isRequired,
  onFilters: PropTypes.func.isRequired,
};